import React from "react";
import { motion } from "framer-motion";
import { MagnifyingGlass, ShoppingCart, Confetti } from "@phosphor-icons/react";

const steps = [
  {
    icon: MagnifyingGlass,
    number: "01",
    title: "Busca",
    description: "Explora cientos de restaurantes y platos cerca de ti. Filtra por tipo de comida, precio o valoración.",
    color: "bg-primary/10",
    iconColor: "text-primary",
  },
  {
    icon: ShoppingCart,
    number: "02",
    title: "Pide",
    description: "Añade tus platos favoritos al carrito y personaliza tu orden. Pago seguro en segundos.",
    color: "bg-accent/10",
    iconColor: "text-accent",
  },
  {
    icon: Confetti,
    number: "03",
    title: "Disfruta",
    description: "Sigue tu pedido en tiempo real y recíbelo caliente en tu puerta. ¡Buen provecho!",
    color: "bg-secondary/10",
    iconColor: "text-secondary",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const HowItWorks = () => {
  return (
    <section id="como-funciona" className="py-24 bg-muted/30" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="uppercase text-xs tracking-[0.2em] font-bold text-primary">
            Simple y Rápido
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground mt-4">
            ¿Cómo Funciona?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
            Pedir tu comida favorita nunca fue tan fácil. Solo tres pasos 
            te separan de una deliciosa experiencia.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              variants={itemVariants}
              className="step-card neumorphic rounded-2xl p-8 relative group cursor-pointer"
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              data-testid={`step-card-${index + 1}`}
            >
              {/* Step Number */}
              <span className="absolute top-6 right-6 text-6xl font-black text-muted/50 select-none">
                {step.number}
              </span>

              {/* Icon */}
              <div className={`${step.color} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <step.icon className={`w-8 h-8 ${step.iconColor}`} weight="duotone" />
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-foreground mb-3">
                {step.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Connection Line - Hidden on mobile */}
        <div className="hidden md:block relative mt-8">
          <div className="absolute top-0 left-[16.67%] right-[16.67%] h-0.5 bg-gradient-to-r from-primary via-accent to-secondary opacity-20" />
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
